import moment from 'moment'

// Pure helpers for the BioTrack to S2S migration wizard.

export const MIGRATION_STEPS = [
  'Validate token',
  'Fetch inventory',
  'Compare batches',
  'Switch over',
]

export const MIGRATION_PRIMARY_LABELS = [
  'Next: fetch inventory',
  'Next: compare batches',
  'Next: review changes',
  'Switch over',
]

export const DIFF_FILTERS = Object.freeze({ ALL: 'all', MATCHED: 'matched', MISSING: 'missing' })
export const DIFF_PAGE_SIZE = 8
export const DIFF_CSV_FILENAME = 'biotrack-s2s-batch-diff.csv'
export const DIFF_CSV_PARTIAL_FILENAME = 'biotrack-s2s-batch-diff-partial.csv'

export const NM_CCD_SYNC_MODEL_TYPE = 'SYNC_PACKAGES'
export const SYNC_POLL_INTERVAL_MS = 3000
export const SYNC_POLL_TIMEOUT_MS = 10 * 60 * 1000
export const SYNC_TIMEOUT_MESSAGE = 'The S2S inventory sync did not finish within 10 minutes.'
export const SYNC_NOT_STARTED_MESSAGE =
  'The S2S inventory sync did not start. Check that NM CCD is enabled for this shop, then try again.'

export const TASK_STATUS = Object.freeze({
  PENDING: 'pending',
  RUNNING: 'running',
  DONE: 'done',
  FAILED: 'failed',
})

// Order matters: the auto-map matches against inventory the NM CCD sync persists, and that sync
// only runs for an enabled account, so NM CCD is enabled first. BioTrack is disabled last so the
// shop never has no active compliance provider.
export const SWITCH_OVER_TASKS = Object.freeze([
  { key: 'enable', label: 'Enable NM CCD and sync inventory' },
  { key: 'map', label: 'Move batches to S2S' },
  { key: 'disable', label: 'Disable BioTrack' },
])

export const formatCount = (value) => Number(value || 0).toLocaleString('en-US')

export const batchCount = (value) =>
  `${formatCount(value)} ${Number(value) === 1 ? 'batch' : 'batches'}`

export const formatTime = (value, pattern = 'h:mm A') =>
  value ? moment(value).format(pattern) : ''

// `GET mgmt/compliance/syncjobs/check` returns the active job, or an empty body once it settled.
export const isComplianceSyncJobActive = (job) =>
  Boolean(job && typeof job === 'object' && (job.id || job.status))

export const normalizeMappingPreview = (response = {}) => {
  const rows = (response.rows || []).map((row) => ({
    batchId: row.batchId || '',
    batch: row.batchLabel || '',
    product: row.productName || '',
    qty: row.quantity === null || row.quantity === undefined ? '' : String(row.quantity),
    s2s: row.matched && row.s2sBarcode ? row.s2sBarcode : '',
    matched: Boolean(row.matched),
    reason: row.reason || '',
  }))
  const matchedRows = rows.filter((row) => row.matched).length
  const count = (value, fallback) =>
    Number(value === undefined || value === null ? fallback : value)
  return {
    s2sPackageCount: count(response.s2sPackageCount, 0),
    blazeBatchCount: count(response.blazeBatchCount, rows.length),
    matchedCount: count(response.matchedCount, matchedRows),
    unmatchedCount: count(response.unmatchedCount, rows.length - matchedRows),
    fetchedAt: response.fetchedAt || Date.now(),
    truncated: Boolean(response.truncated),
    rows,
  }
}

export const filterDiffRows = (rows = [], query = '', filter = DIFF_FILTERS.ALL) => {
  const term = String(query || '')
    .trim()
    .toLowerCase()
  return rows.filter((row) => {
    if (filter === DIFF_FILTERS.MATCHED && !row.matched) return false
    if (filter === DIFF_FILTERS.MISSING && row.matched) return false
    if (!term) return true
    return row.batch.toLowerCase().includes(term) || row.product.toLowerCase().includes(term)
  })
}

const csvCell = (value) => {
  const text = String(value === null || value === undefined ? '' : value)
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

export const buildDiffCsv = (rows = []) =>
  [
    'batch,product,quantity,s2s_package,status',
    ...rows.map((row) =>
      [
        row.batch,
        row.product,
        row.qty,
        row.matched ? row.s2s : '',
        row.matched ? 'matched' : 'not_in_s2s',
      ]
        .map(csvCell)
        .join(','),
    ),
  ].join('\n')

export const diffCsvFilename = (truncated) =>
  truncated ? DIFF_CSV_PARTIAL_FILENAME : DIFF_CSV_FILENAME

export const saveTextFile = (text, filename, type = 'text/csv') => {
  const url = URL.createObjectURL(new Blob([text], { type }))
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  setTimeout(() => URL.revokeObjectURL(url), 2000)
}

export const findBiotrackLocation = (biotrackAccount = {}, shopId) =>
  (biotrackAccount.locations || []).find((location) => location.shopId === shopId) || null

export const disableBiotrackLocation = (biotrackAccount = {}, shopId) => ({
  ...biotrackAccount,
  locations: (biotrackAccount.locations || []).map((location) =>
    location.shopId === shopId
      ? { ...location, enabled: false, deliveryEnabled: false, enableSync: false }
      : location,
  ),
})

// Migrate does not need a saved NM CCD account: step 1 of the wizard collects the token.
export const canMigrateToNmCcd = ({ showNmCcdTab, canEnableNmCcd }) =>
  Boolean(showNmCcdTab && canEnableNmCcd)

export const TOKEN_REQUIRED_MESSAGE = 'Paste the location token issued by NM CCD.'

export const validatedIdentity = (result = {}) => {
  const metadata = result.metadata || {}
  return {
    ...(metadata.organizationId ? { nmCcdOrganizationId: metadata.organizationId } : {}),
    ...(metadata.locationId ? { nmCcdLocationId: metadata.locationId } : {}),
  }
}

export const initialSwitchOverTasks = () =>
  SWITCH_OVER_TASKS.map((task) => ({ ...task, status: TASK_STATUS.PENDING, detail: 'Waiting' }))

export const migrationErrorMessage = (error, fallback = 'The request failed. Try again.') => {
  const data = (error && error.response && error.response.data) || error || {}
  const candidates = [data.errorMessage, data.message, error && error.message]
  const message = candidates.find((value) => typeof value === 'string' && value.trim())
  return message ? message.trim() : fallback
}

export const describeSwitchOverFailure = (tasks = []) => {
  const failed = tasks.find((task) => task.status === TASK_STATUS.FAILED)
  return {
    title: failed ? `Switch over stopped at "${failed.label}".` : 'Switch over stopped.',
    applied: tasks.filter((task) => task.status === TASK_STATUS.DONE).map((task) => task.label),
    notApplied: tasks.filter((task) => task.status !== TASK_STATUS.DONE).map((task) => task.label),
  }
}



// WEBPACK FOOTER //
// ./src/utils/nmCcdMigration.js